import React, { Component } from 'react'
import { connect } from 'react-redux'
import { ThunkDispatch } from 'redux-thunk'
import {RouteComponentProps, withRouter} from "react-router-dom";

// Utils.
import { cloneDeep, uniqBy } from 'lodash';
import {
    Tooltip,
    Position,
} from "@blueprintjs/core";

// Icons.
import { FaSearch, FaCircle, FaTrash, FaTimes, FaHeart, FaRegHeart } from 'react-icons/fa';

// Components.
import SidebarMenu from './SidebarMenu';
import CreateOrderModal from '../b2b/CreateOrderModal'
import MyMoloOverlay from '../b2b/MyMoloOverlay';
import ProductDownloadModal from '../b2b/ProductDownloadModal';
import SearchBox from '../shared/SearchBox';
import ShopDropDown from '../b2b/ShopDropdown';
import Dropdown from './Dropdown';
import DeleteOrderModal from '../b2b/DeleteOrderModal';
import CountDown2 from '../shared/CountDown2';

// Models.
import { Filter } from '../../models/Filter';

// Store.
import { RootState } from '../../store';
import { ICollectionState  } from '../../store/collections/reducers'
import { IProductState  } from '../../store/products/reducers'
import { IFilterState } from '../../store/session/reducers'
import { IUserSate } from '../../store/user/reducers'
import { IWishlistState } from '../../store/wishlist/reducers';
import { IOrderState } from '../../store/order/orderStore';
import { applyFilter, getFilter } from '../../store/session/actions'
import { logoutUser } from '../../store/user/actions';
import { resetFilter } from '../../store/session/actions'

import { DeliveryWindow } from '../../models/Style';
import { getDashboardOrderState, activityFabVisibilityChanged } from '../../store/order/actions';
import AutoUpdateHeaderStatus from '../b2b/AutoUpdateHeaderStatus';
import { DEFAULT_WISHLIST_NAME } from '../../constants/wishlistConfig';
import {
    captureShopListScrollForPathname,
    parseShopOrderCollectionFromPath,
    readLastShopReturnContext,
    resolveWishlistShopBackTarget,
    closeShopUtilityPage,
} from '../../utils/lastShopReturnContext';
import { readProductListScrollTop } from '../../utils/shopListScroll';
import {
    isB2bProductOverlayOpen,
    requestCloseB2bProductOverlay,
} from '../../utils/productOverlayHistory';
import {
    isActivityFabVisible,
    setActivityFabVisible,
} from '../../utils/orderActivityFabStorage';
import { resolveCurrentEditableOrderContext } from '../../utils/orderRouteUtils';

interface State {
    showSidebar: boolean,
    showOrderModal: boolean,
    collectionName: string,
    orderTypeName: string,
    showNavOrderModal: boolean,
    selectedCollection: any,
    isCheckoutPage: boolean,
    showSearch: boolean,
    showShopOverlay: boolean,
    showProductDownload: boolean
    showDeleteOrderModal: boolean,
    orderIdToDelete?: number
    showMyMolo: boolean
    myMoloIsProfile: boolean
    wishlistRemoteSyncPulse: boolean
}
  
interface OwnProps {
    match?: {params: {collection: string, orderType: string}},
    children?: any
    history?: any
    location?: any
    staticContext?: any
    isDashboard?: boolean
    editMode?: boolean
}
  
interface DispatchProps {
    applyFilter: (filter: Filter) => void;
    getFilter: () => void;
    logoutUser: () => void;
    getDashboardOrderState: (orderId: number) => void;
    resetFilter: (filter: Filter) => void;
    activityFabVisibilityChanged: () => void;
}
  
interface StateProps {
    productState: IProductState
    collectionState: ICollectionState
    filterState: IFilterState
    userState: IUserSate
    orderState: IOrderState
    wishlist: IWishlistState
    activityFabVisibleNonce: number
}
  
type Props = StateProps & OwnProps & DispatchProps & RouteComponentProps;

export class Header extends Component<Props, State> {
    wishlistPulseTimer: number | null = null;

    state: State = {
        showSidebar: false,
        showOrderModal: false,
        collectionName: '',
        orderTypeName: '',
        showNavOrderModal: false,
        showProductDownload: false,
        selectedCollection: null,
        isCheckoutPage: false,
        showSearch: false,
        showShopOverlay: false,
        showDeleteOrderModal: false,
        showMyMolo: false,
        myMoloIsProfile: false,
        wishlistRemoteSyncPulse: false,
    }

    childRef = React.createRef<SidebarMenu>();

    componentDidMount() {
        this.setMenuItems();

        if (this.props.location.pathname.includes('/checkout')) {
            this.setState({isCheckoutPage: true})
        }
    }

    componentWillUnmount() {
        if (this.wishlistPulseTimer !== null) {
            window.clearTimeout(this.wishlistPulseTimer);
        }
    }

    playWishlistRemoteSyncPulse = () => {
        this.setState({ wishlistRemoteSyncPulse: false }, () => {
            this.setState({ wishlistRemoteSyncPulse: true });
        });

        if (this.wishlistPulseTimer !== null) {
            window.clearTimeout(this.wishlistPulseTimer);
        }

        this.wishlistPulseTimer = window.setTimeout(() => {
            this.setState({ wishlistRemoteSyncPulse: false });
            this.wishlistPulseTimer = null;
        }, 2200);
    };

    componentDidUpdate(prevProps: Props) {
        if (this.props.location !== prevProps.location) {
            this.setMenuItems();

            if (this.props.location && this.props.location.pathname && this.props.location.pathname.includes('/checkout')) {
                this.setState({isCheckoutPage: true})
            } else {
                this.setState({isCheckoutPage: false})
            }
        }

        if (
            prevProps.wishlist.remoteSyncNonce !== this.props.wishlist.remoteSyncNonce &&
            this.props.wishlist.remoteSyncNonce > 0
        ) {
            this.playWishlistRemoteSyncPulse();
        }
    }

    setMenuItems = () => {
        let collectionName = '';
        let orderTypeName = '';

        if (this.props.match && this.props.match.params) {
            const c = this.props.match.params.collection;
            const o = this.props.match.params.orderType;
            if (c !== undefined && c !== null) {
                collectionName = String(c);
            }
            if (o !== undefined && o !== null) {
                orderTypeName = String(o);
            }
        }

        const pathname = (this.props.location && this.props.location.pathname) || '';
        const fromPath = parseShopOrderCollectionFromPath(pathname);
        if (fromPath) {
            if (!orderTypeName) {
                orderTypeName = fromPath.orderType;
            }
            if (!collectionName) {
                collectionName = fromPath.collection;
            }
        }

        // Do not restore collection from session on /shop/:orderType (chooser) — would wrongly show e.g. MAIN.
        const shopSegments = pathname.split('/').filter(Boolean);
        const isShopOrderTypeOnly =
            shopSegments.length === 2 &&
            shopSegments[0] === 'shop' &&
            shopSegments[1] !== 'wishlist';

        if (
            !collectionName &&
            orderTypeName &&
            !pathname.startsWith('/shop/wishlist/') &&
            !isShopOrderTypeOnly
        ) {
            const saved = readLastShopReturnContext();
            if (saved) {
                const fromSaved = parseShopOrderCollectionFromPath(saved.pathname);
                if (fromSaved && fromSaved.orderType === orderTypeName) {
                    collectionName = fromSaved.collection;
                }
            }
        }

        this.setState({
            collectionName,
            orderTypeName,
        });
    }

    /** Delivery rows for header: prefer Redux master list, else filter facet strings. */
    getHeaderDeliveryWindowItems = (): DeliveryWindow[] => {
        const fromStore = this.props.collectionState.deliveryWindows;
        if (fromStore && fromStore.length) {
            return fromStore;
        }
        const names = this.props.filterState.filter && this.props.filterState.filter.deliveryWindows;
        if (names && names.length) {
            return names
                .filter((n): n is string => !!n)
                .map((label) => ({ label } as DeliveryWindow));
        }
        return [];
    }

    toggleSidebar = (open: boolean) => {
        this.setState({ showSidebar: open })
    }

    hide = () => {}

    onClickSetCollectionType = (collection: any) => {
        // if (this.props.filterState.filter) {
        //     this.props.resetFilter(this.props.filterState.filter)
        // }
        if (this.props.editMode) {
            window.location.href = '/edit/' + collection.collectionName
            // this.props.history.push('/edit/' + collection.collectionName);
        } else {
            window.location.href = '/shop/' + this.state.orderTypeName + '/'  + collection.collectionName
            // this.props.history.push('/shop/' + this.state.orderTypeName + '/'  + collection.collectionName);
        }
    }

    onClickSetOrderType = (orderType: any) => {
        this.props.history.push('/shop/' + orderType.orderType);
    }

    onClickSetDeliveryType = (deliveryWindow: DeliveryWindow | string) => {
        const newFilter = cloneDeep(this.props.filterState.filter);
        if (!newFilter) return


        if (typeof deliveryWindow === 'string') {
            newFilter.activeFilters.deliveryWindows = [];
        } else {
            newFilter.activeFilters.deliveryWindows = [deliveryWindow.label];
        }

        this.props.applyFilter(newFilter);
    }

    onClickSetSustainability = (sustainability: string) => {
        const newFilter = cloneDeep(this.props.filterState.filter);

        if (!newFilter || !newFilter.activeFilters) {
            return;
        }
        if (!Array.isArray(newFilter.activeFilters.sustainability)) {
            newFilter.activeFilters.sustainability = [];
        }

        const active = newFilter.activeFilters.sustainability;
        const indexOf = active.indexOf(sustainability);

        if (sustainability === 'all') {
            newFilter.activeFilters.sustainability = [];
        } else if (indexOf > -1) {
            active.splice(indexOf, 1);
        } else {
            active.push(sustainability);
        }

        this.props.applyFilter(newFilter);
    }

    onClickSearchIcon = () => {
        this.setState({showSearch: !this.state.showSearch})
    }

    onClickShopIcon = () => {
        if (this.state.showShopOverlay) {
            this.setState({showShopOverlay: false})
        }
        this.setState({showShopOverlay: true})
    }

    showCreateOrderModal = () => {
        this.setState({ showOrderModal: true, showShopOverlay: false })
    }

    openNavOrderModal = () => {
        if (this.childRef.current) {
            // this.childRef.current.toggle();
            this.setState({ showNavOrderModal: true })
        }
    }

    showDeleteOrderModal = () => {
        this.setState({ showDeleteOrderModal: true })
    }

    onClickLinkHandler = () => {
        if (this.childRef.current) {
            // this.childRef.current.toggle();
        }
        this.toggleSidebar(false)
    }

    onClickMyMolo = () => {
        this.props.history.push('/my-orders')
    }

    onClickProfile = () => {
        this.setState((prev) => {
            const opening = !prev.showMyMolo;
            return { showMyMolo: opening, myMoloIsProfile: opening };
        });
    }

    onClickProductDownload = () => {
        this.props.history.push('/download')
    }

    onClickOrderHistory = () => {
        this.props.history.push('/activity-history')
    }

    getOrderActivityContext = () =>
        resolveCurrentEditableOrderContext(
            {
                order: this.props.orderState.order,
                dashboardOrder: this.props.orderState.dashboardOrder,
            },
            this.props.location.pathname
        );

    getUserEmail = () =>
        this.props.userState.user && this.props.userState.user.email
            ? this.props.userState.user.email
            : null;

    handleOrderActivityToggle = () => {
        const context = this.getOrderActivityContext();
        const userEmail = this.getUserEmail();
        if (!context || !userEmail) {
            return;
        }

        const nextVisible = !isActivityFabVisible(userEmail, context.orderId);
        setActivityFabVisible(userEmail, context.orderId, nextVisible);
        this.props.activityFabVisibilityChanged();
    };

    logoutUser = () => {
        // this.props.history.push('/logout')
        // window.history.replaceState(null, '', "/");
        // this.props.history.replace(null, '', "/");
        this.props.history.push('/')
        this.props.logoutUser();
    }

    onCLickChangeDashboardOrder = (orderId: number) => {
        this.props.history.push('/dashboard/' + orderId);
        this.props.getDashboardOrderState(orderId);
    }

    upcomingLabel = (deliveryWindow: DeliveryWindow) => {
        if (deliveryWindow.label !== 'available now') {
            return <sub className="extra-label"> - upcoming</sub>
        }

        return null;
    }

    renderLinks = () => {
        const deliveryItems = this.getHeaderDeliveryWindowItems();
        const activeDeliveryLabel =
            this.props.filterState.filter &&
            this.props.filterState.filter.activeFilters.deliveryWindows[0];
        const showDeliveryDropdown =
            !!this.state.collectionName &&
            !!this.props.filterState.filter &&
            deliveryItems.length > 0;

        return (
            <ul className="top-menu top-menu--header-toolbar">
                {
                    this.props.productState.orderTypes &&
                    this.state.orderTypeName &&
                    this.props.productState.orderTypes.length ?
                    <li className="top-menu__item">
                        <Dropdown
                            items={this.props.productState.orderTypes || []}
                            value={this.props.productState.orderTypes.find(
                                type => type.orderType === this.state.orderTypeName)
                            }
                            identifier={'orderType'}
                            onSelect={this.onClickSetOrderType}
                        />
                    </li> : null
                }
                {
                    this.props.collectionState.collections && this.props.collectionState.collections.length &&
                    this.state.orderTypeName ?
                    <li className="top-menu__item">
                        <Dropdown
                            items={this.props.collectionState.collections || []}
                            value={
                                this.state.collectionName
                                    ? this.props.collectionState.collections.find(
                                        col => col.collectionName === this.state.collectionName
                                    )
                                    : undefined
                            }
                            identifier={'collectionName'}
                            onSelect={this.onClickSetCollectionType}
                            placeholder={'Collection'}
                        />
                    </li> : null
                }
                {
                    showDeliveryDropdown ?
                    <li className="top-menu__item desktop-only" >
                        <Dropdown
                            items={deliveryItems}
                            value={deliveryItems.find((del) =>
                                activeDeliveryLabel ? del.label === activeDeliveryLabel : false
                            )}
                            identifier={'label'}
                            onSelect={this.onClickSetDeliveryType}
                            allLabel='Show All'
                            allOption={true}
                            placeholder={'Deliveries All'}
                            extraLabel={this.state.orderTypeName && this.state.orderTypeName.toLowerCase() === 'reorder' && this.upcomingLabel}
                        />
                    </li> : null
                }
                {
                    this.state.collectionName &&
                    this.props.filterState.filter && this.props.filterState.filter.sustainability ?
                    <li className="top-menu__item desktop-only">
                        <Dropdown
                            items={this.props.filterState.filter.sustainability}
                            value={this.props.filterState.filter.activeFilters.sustainability}
                            identifier={'$index'}
                            onSelect={this.onClickSetSustainability}
                            allLabel='Show All'
                            allOption={true}
                            placeholder='SUSTAINABILITY'
                            multi={true}
                        />
                    </li> : null
                }
            </ul>
        )
    }

    renderShopNavigationToolbar = () => {
        const filter = this.props.filterState.filter;
        const hasSustain =
            filter && filter.sustainability && filter.sustainability.length > 0;
        const sustainValue = (filter && filter.activeFilters && filter.activeFilters.sustainability) || [];

        const orderTypes = this.props.productState.orderTypes;
        const hasOrderTypes = !!(orderTypes && orderTypes.length);
        const orderTypeValue =
            hasOrderTypes && this.state.orderTypeName
                ? orderTypes.find((t) => t.orderType === this.state.orderTypeName)
                : undefined;

        const returnCtx = readLastShopReturnContext();
        const shopReturn =
            returnCtx &&
            returnCtx.pathname &&
            !returnCtx.pathname.startsWith('/shop/wishlist/') &&
            parseShopOrderCollectionFromPath(returnCtx.pathname)
                ? returnCtx
                : null;

        return (
            <div className="header-main__icons__wishlist-toolbar">
                {shopReturn ? (
                    <div className="top-menu__item header-main__icons__wishlist-toolbar__item">
                        <button
                            type="button"
                            className="header-main__icons__wishlist-back-trigger"
                            onClick={() => this.props.history.push(shopReturn.pathname)}
                        >
                            {'Go back to ' + shopReturn.title}
                        </button>
                    </div>
                ) : hasOrderTypes ? (
                    <div className="top-menu__item header-main__icons__wishlist-toolbar__item">
                        <Dropdown
                            items={orderTypes}
                            value={orderTypeValue}
                            identifier={'orderType'}
                            onSelect={this.onClickSetOrderType}
                            placeholder={'ORDER TYPE'}
                        />
                    </div>
                ) : (
                    <div className="top-menu__item header-main__icons__wishlist-toolbar__item">
                        <button
                            type="button"
                            className="header-main__icons__wishlist-back-trigger"
                            onClick={() => this.props.history.push('/shop')}
                        >
                            Back to front page
                        </button>
                    </div>
                )}
                {hasSustain && (
                    <div className="top-menu__item desktop-only header-main__icons__wishlist-toolbar__item header-main__icons__wishlist-toolbar__dropdown">
                        <Dropdown
                            items={filter!.sustainability}
                            value={sustainValue}
                            identifier={'$index'}
                            onSelect={this.onClickSetSustainability}
                            allLabel="Show All"
                            allOption={true}
                            placeholder="SUSTAINABILITY"
                            multi={true}
                        />
                    </div>
                )}
            </div>
        );
    };

    /** Same target as wishlist toolbar “Go back to …” / “Back to front page”. */
    private getWishlistShopBackTarget = () => resolveWishlistShopBackTarget();

    private navigateToWishlist = (wishlistPath: string) => {
        const pathname =
            this.props.location && this.props.location.pathname
                ? this.props.location.pathname
                : '';
        if (pathname) {
            captureShopListScrollForPathname(pathname, readProductListScrollTop());
        }
        this.props.history.push(wishlistPath);
    };

    private leaveWishlistShop = () => {
        const { pathname } = this.getWishlistShopBackTarget();
        this.props.history.push(pathname);
    };

    private handleWishlistShopHeaderBack = () => {
        if (isB2bProductOverlayOpen()) {
            requestCloseB2bProductOverlay();
            return;
        }
        this.leaveWishlistShop();
    };

    renderWishlistHeaderIcon = () => {
        const locPath =
            this.props.location && this.props.location.pathname ? this.props.location.pathname : '';
        const onWishlistShop = locPath.includes('/shop/wishlist/');

        if (onWishlistShop) {
            const { ariaLabel } = this.getWishlistShopBackTarget();
            return (
                <div
                    className="header-main__icons__icon header-wishlist-trigger header-wishlist-trigger--back clickable"
                    data-wishlist-target="header-icon"
                    role="button"
                    tabIndex={0}
                    aria-label={ariaLabel}
                    onClick={this.handleWishlistShopHeaderBack}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            this.handleWishlistShopHeaderBack();
                        }
                    }}
                >
                    <span className="header-wishlist-trigger__glyph">
                        <FaTimes aria-hidden />
                    </span>
                </div>
            );
        }

        const keys =
            this.props.wishlist && this.props.wishlist.itemKeys
                ? Object.keys(this.props.wishlist.itemKeys)
                : [];
        const hasItems = keys.length > 0;
        const wishlistPath = '/shop/wishlist/' + encodeURIComponent(DEFAULT_WISHLIST_NAME);
        const glyphClassName =
            'header-wishlist-trigger__glyph' +
            (this.state.wishlistRemoteSyncPulse ? ' header-wishlist-trigger__glyph--remote-sync' : '');
        return (
            <div
                className={
                    'header-main__icons__icon header-wishlist-trigger' +
                    (hasItems ? ' header-wishlist-trigger--has clickable' : ' header-wishlist-trigger--empty')
                }
                data-wishlist-target="header-icon"
                role="button"
                aria-disabled={!hasItems}
                aria-label={hasItems ? 'View wishlist' : 'No favorites on wishlist'}
                tabIndex={hasItems ? 0 : -1}
                onClick={() => {
                    if (hasItems) {
                        this.navigateToWishlist(wishlistPath);
                    }
                }}
                onKeyDown={(e) => {
                    if (!hasItems) {
                        return;
                    }
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        this.navigateToWishlist(wishlistPath);
                    }
                }}
            >
                <span className={glyphClassName}>
                    {hasItems ? <FaHeart aria-hidden /> : <FaRegHeart aria-hidden />}
                </span>
            </div>
        );
    }

    renderOrderDropdownItem = (order: any, useLabel: boolean) => (
        <div className="order-dropdown flex">
            {useLabel && order.navImportRef && order.orderIsFinal && <span className="order-dropdown__label --completed">Approved order</span>}
            {useLabel && !order.orderIsFinal && order.navImportRef && <span className="order-dropdown__label --completed">Access to edit</span>}
            {useLabel && !order.navImportRef && <span className="order-dropdown__label">Open order</span>}
            <span>{ order.sellToCustomerName }</span>
            <span className="flex items-center pt-1"><FaCircle className="order-dropdown__divider" /></span>
            <span> 
                 b2b{order.appOrderId}</span>
            <span className="flex items-center pt-1"><FaCircle className="order-dropdown__divider" /></span>
            <span>
                 {order.orderType}</span>
            {order.navImportRef && <span className="flex items-center pt-1"><FaCircle className="order-dropdown__divider" /></span>}
            {
                order.navImportRef &&
                <span>
                     {order.navImportRef}
                </span>
            }
            {order.salespersonNote && <span className="flex items-center pt-1"><FaCircle className="order-dropdown__divider" /></span>}
            {
                order.salespersonNote &&
                <span>
                     { order.salespersonNote }
                </span>
            }

            {!useLabel && order.navImportRef && order.orderIsFinal && <span className="order-dropdown__label --small --completed">Approved order</span>}
            {!useLabel && order.navImportRef && !order.orderIsFinal && <span className="order-dropdown__label --small --completed">Access to edit</span>}
            {!useLabel && !order.navImportRef && <span className="order-dropdown__label --small">Open order</span>}
        </div>
    )

    renderShopUtilityPageCloseIcon = () => {
        const locPath =
            this.props.location && this.props.location.pathname ? this.props.location.pathname : '';

        const closeLabels: Record<string, string> = {
            '/activity-history': 'Close activity history',
            '/my-orders': 'Close my orders',
        };
        const ariaLabel = closeLabels[locPath];

        if (!ariaLabel) {
            return null;
        }

        return (
            <div
                className="header-main__icons__icon header-wishlist-trigger header-wishlist-trigger--back clickable"
                role="button"
                tabIndex={0}
                aria-label={ariaLabel}
                title="Close (Esc)"
                onClick={() => closeShopUtilityPage(this.props.history)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        closeShopUtilityPage(this.props.history);
                    }
                }}
            >
                <span className="header-wishlist-trigger__glyph">
                    <FaTimes aria-hidden />
                </span>
            </div>
        );
    };

    render() {
        const { order, orders } = this.props.orderState;
        const dashboardOrder = order;
        let dropDownItems: any[] = []

        // console.log(this.props.orderState);

        let hoursLeft = 0;

        if (this.props.editMode && this.props.orderState.order) {
            const diff = new Date(new Date().toISOString()).getTime() - new Date(this.props.orderState.order.orderheader.createdUtc).getTime()
            hoursLeft = (3600000 * 24) - diff;
        }

        if (dashboardOrder) {
            dropDownItems = orders.filter(order => order.appOrderId !== dashboardOrder.orderheader.appOrderId) || [];
            const recent = this.props.orderState.recentClosedOrders.filter(order => order.navImportRef && order.appOrderId !== dashboardOrder.orderheader.appOrderId) || [];
            dropDownItems = [...dropDownItems, ...recent]
        }

        dropDownItems = uniqBy(dropDownItems, 'appOrderId');

        const onWishlistShop =
            this.props.location &&
            this.props.location.pathname &&
            this.props.location.pathname.includes('/shop/wishlist/');

        const locPath =
            this.props.location && this.props.location.pathname ? this.props.location.pathname : '';
        const onActivityHistoryPage = locPath === '/activity-history';
        const onMyOrdersPage = locPath === '/my-orders';
        const showShopNavigationToolbar = onWishlistShop || onActivityHistoryPage || onMyOrdersPage;
        const orderActivityContext = this.getOrderActivityContext();
        const userEmail = this.getUserEmail();
        const orderActivityVisible = orderActivityContext && userEmail
            ? isActivityFabVisible(userEmail, orderActivityContext.orderId)
            : false;

        return (
            <div className={'header' + (this.props.editMode ? ' --edit-mode' : '')}>
                <div className="header__main">
                    <div className="molo-logo" onClick={() => {
                        if (window.innerWidth > 900) {
                            this.props.history.push('/shop')
                        }
                    }}>
                        {
                            this.props.userState.user &&
                            <span className="--user-name">{this.props.userState.user.customerData.Name}</span>
                        }
                    </div>
                    {
                        this.props.isDashboard && !this.state.isCheckoutPage && dashboardOrder ?
                        <ul className={'top-menu' + (this.props.isDashboard ? ' top-menu--dashboard' : '')}>
                            <li className="top-menu__item desktop-only">
                                <Dropdown
                                    items={dropDownItems}
                                    value={
                                        () => {
                                            if (dashboardOrder.orderheader.navImportRef &&  !dashboardOrder.orderheader.orderState) {
                                                return dashboardOrder
                                            }
                                            return orders.find(o => o.appOrderId === dashboardOrder.orderheader.appOrderId)
                                        }
                                    }
                                    trigger={() => this.renderOrderDropdownItem(dashboardOrder.orderheader, true)}
                                    valueRender={(item: any) => this.renderOrderDropdownItem(item, false)}
                                    identifier={'appOrderId'}
                                    onSelect={(item: any) => this.onCLickChangeDashboardOrder(item.appOrderId)}
                                />
                            </li>
                            <li className="top-menu__item desktop-only">
                                <div className="header-main__icons__icon clickable" style={{height: '100%'}}>
                                    
                                        <Tooltip
                                            content={<div>Delete order</div>}
                                            position={Position.RIGHT}
                                        >
                                            <span onClick={ (e) => this.setState({ showDeleteOrderModal: true }) }>
                                                {
                                                    dashboardOrder && dashboardOrder.orderheader.orderIsFinal ?
                                                    <FaTimes size="24px" /> : <FaTrash size="18px" />
                                                }
                                            </span>
                                        </Tooltip>
                                </div>
                            </li>
                            <li className="top-menu__item mobile-only">
                                <div className="dropdown">
                                    <div className="dropdown__trigger">
                                        <div className="order-dropwdown">
                                            <span>{ dashboardOrder.orderheader.sellToCustomerName }</span>
                                            <span> <FaCircle className="order-dropdown__divider" /> b2b{dashboardOrder.orderheader.appOrderId}</span>
                                            <span> <FaCircle className="order-dropdown__divider" /> {dashboardOrder.orderheader.orderType}</span>
                                            {
                                                dashboardOrder.orderheader.navImportRef &&
                                                <span>
                                                    <FaCircle className="order-dropdown__divider" /> {dashboardOrder.orderheader.navImportRef}
                                                </span>
                                            }
                                            {
                                                dashboardOrder.orderheader.salespersonNote &&
                                                <span>
                                                    <FaCircle className="order-dropdown__divider" /> { dashboardOrder.orderheader.salespersonNote }
                                                </span>
                                            }
                                        </div>
                                    </div>
                                </div>
                            </li>
                        </ul>
                        :
                        null
                    }
                    {
                        this.props.isDashboard && this.state.isCheckoutPage && this.props.orderState.order ?
                        <ul className={'top-menu' + (this.props.isDashboard ? ' top-menu--dashboard' : '')}>
                            <li className="top-menu__item">
                                <div className="dropdown">
                                    <div className="dropdown__trigger">
                                        {this.renderOrderDropdownItem(this.props.orderState.order.orderheader, true)}
                                    </div>
                                </div>
                            </li>
                        </ul>
                        :
                        null
                    }
                    <div className="header-main__icons">
                        {!this.state.isCheckoutPage && showShopNavigationToolbar && !this.props.isDashboard && this.renderShopNavigationToolbar()}
                        {!this.state.isCheckoutPage && !showShopNavigationToolbar && this.renderLinks()}
                        <div className="header-main__icons__static">
                            <AutoUpdateHeaderStatus />
                            <div className="header-main__icons__icon clickable">
                                <ShopDropDown
                                    hideDelete={!this.props.orderState.order}
                                    onClickCreateOrder={this.showCreateOrderModal}
                                    onClickDeleteOrder={this.showDeleteOrderModal}
                                    logoutUser={this.logoutUser}
                                    onClickMyMolo={this.onClickMyMolo}
                                    onClickOrderHistory={this.onClickOrderHistory}
                                    onClickProductDownload={this.onClickProductDownload}
                                    editMode={this.props.editMode}
                                    onClickProfile={this.onClickProfile}
                                    showProductDownload={true}
                                    orderActivityToggleEnabled={!!orderActivityContext}
                                    orderActivityVisible={orderActivityVisible}
                                    onOrderActivityToggle={this.handleOrderActivityToggle}
                                    deleteOrderText={
                                        this.props.orderState.order && this.props.orderState.order.orderheader.navImportRef ?
                                    'Close order' : 'Delete current order'  }
                                />
                            </div>
                            {
                                this.props.editMode && this.props.orderState.order &&
                                <div>
                                    <CountDown2
                                        from={hoursLeft}
                                        dateFrom={this.props.orderState.order.orderheader.createdUtc}
                                        limit={2}
                                    />
                                </div>
                            }
                            {!this.state.isCheckoutPage && !this.props.isDashboard && this.renderShopUtilityPageCloseIcon()}
                            {!this.state.isCheckoutPage && !this.props.isDashboard && this.renderWishlistHeaderIcon()}
                            <div className="header-main__icons__icon clickable search-trigger" onClick={this.onClickSearchIcon}>
                                <FaSearch className="search-trigger" />
                            </div>
                        </div>
                    </div>
                </div>
        
                <CreateOrderModal
                    isShowing={this.state.showOrderModal}
                    hide={() => this.setState({ showOrderModal: false })}
                    orderTypeName={this.state.orderTypeName}
                />
                <SearchBox
                    show={this.state.showSearch}
                    shouldHide={() => this.setState({ showSearch: false })}
                />
                
                <DeleteOrderModal
                    show={this.state.showDeleteOrderModal}
                    shouldHide={() => this.setState({ showDeleteOrderModal: false })}
                    orderId={this.props.orderState.order ? this.props.orderState.order.orderheader.appOrderId : 0}
                    order={this.props.orderState.order && this.props.orderState.order.orderheader}
                    onClickConfirmDelete={(deletedOrder: any) => {
                        if (this.props.orderState.order &&
                            deletedOrder.appOrderId === this.props.orderState.order.orderheader.appOrderId) {
                                this.props.history.push('/');
                        }
                    }}
                />
                <MyMoloOverlay
                    show={this.state.showMyMolo}
                    isProfile={this.state.myMoloIsProfile}
                    shouldHide={() => this.setState({ showMyMolo: false, myMoloIsProfile: false })}
                />

                <ProductDownloadModal
                    show={this.state.showProductDownload}
                    shouldHide={() => this.setState({ showProductDownload: false})}
                />
            </div>
        )
    }
}

const mapStateToProps = (states: RootState, ownProps: OwnProps): StateProps => {
    return {
        productState: states.productStore.productStore,
        collectionState: states.collections.collections,
        filterState: states.session.filter,
        userState: states.user.user,
        orderState: states.order.order,
        wishlist: states.wishlist,
        activityFabVisibleNonce: states.order.order.activityFabVisibleNonce,
    }
}

const mapDispatchToProps = (dispatch: ThunkDispatch<{}, {}, any>, ownProps: OwnProps): DispatchProps => {
    return {
        applyFilter: async (filter: Filter) => {
            await dispatch(applyFilter(filter))
            // console.log('apply filter completed [UI]')
        },
        getFilter: async () => {
            await dispatch(getFilter())
            // console.log('getFIlter completed [UI]')
        },
        logoutUser: async () => {
            await dispatch(logoutUser())
            // console.log('user is invalidated')
        },
        getDashboardOrderState: async (orderId: number) => {
            await dispatch(getDashboardOrderState(orderId));
        },
        resetFilter: async (filter: Filter) => {
            await dispatch(resetFilter(filter))
        },
        activityFabVisibilityChanged: () => {
            dispatch(activityFabVisibilityChanged());
        },
    }
}


export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Header)) 
